
import Vue, { PropType } from "vue";
import { Notification, NotificationFromNolaNovel, NotificationDataType } from "@/lib/models";
import { createUrlWithUtmParams } from "@/lib/utils/url";
import { NolaItemId, NolaPageName } from "@/lib/utils/analytics";

type NotificationType = "agent" | "nolaNovel";

export default Vue.extend<Data, Methods, Computed, Props>({
  props: {
    notifications: {
      type: Array as PropType<Notification[] | NotificationFromNolaNovel[]>,
      required: true,
    },
    notificationType: {
      type: String as PropType<NotificationType>,
      required: true,
    },
    isLoadComplete: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      initialUnreadIds: new Set<string>(),
    };
  },
  watch: {
    notifications: {
      immediate: true,
      handler(newNotifications: Array<Notification | NotificationFromNolaNovel>) {
        // 新しく追加された未読通知のIDを記録（既に記録済みのものは上書きしない）
        newNotifications.forEach((notification) => {
          const key = this.getNotificationKey(notification);
          // まだ記録されていない通知で、未読の場合のみ追加
          if (!this.initialUnreadIds.has(key) && !notification.isRead) {
            this.initialUnreadIds.add(key);
          }
        });
      },
    },
  },
  methods: {
    getNotificationKey(notification: Notification | NotificationFromNolaNovel): string {
      if (this.notificationType === "agent") {
        return (notification as Notification).notificationId;
      }
      return (notification as NotificationFromNolaNovel).typeTargetIdSubTargetId;
    },
    isRead(notification: Notification | NotificationFromNolaNovel): boolean {
      return notification.isRead;
    },
    isInitiallyUnread(notification: Notification | NotificationFromNolaNovel): boolean {
      const key = this.getNotificationKey(notification);
      return this.initialUnreadIds.has(key);
    },
    formatDate(notification: Notification | NotificationFromNolaNovel): string {
      let date: Date;
      if (this.notificationType === "agent") {
        date = new Date((notification as Notification).createdAt);
      } else {
        date = new Date((notification as NotificationFromNolaNovel).publishedAt);
      }
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      return `${year}年${month}月${day}日`;
    },
    getCategoryLabel(notification: Notification | NotificationFromNolaNovel): string {
      if (this.notificationType === "agent") {
        // エージェント通知のカテゴリ
        return "エージェント";
      }
      // Nolaノベル通知のカテゴリ
      const nolaNotif = notification as NotificationFromNolaNovel;
      if (this.isReaderNotification(nolaNotif)) {
        return "読者より";
      }
      if (this.isEditorNotification(nolaNotif)) {
        return "編集者より";
      }
      return "その他";
    },
    getTitle(notification: Notification | NotificationFromNolaNovel): string {
      if (this.notificationType === "agent") {
        // エージェント通知のタイトル（HTMLタグを除去）
        const agentNotif = notification as Notification;
        const div = document.createElement("div");
        div.innerHTML = agentNotif.message;
        const text = div.textContent || div.innerText || "";
        // 最初の行または最初の50文字をタイトルとして使用
        const firstLine = text.split("\n")[0];
        return firstLine.length > 50 ? `${firstLine.substring(0, 50)}...` : firstLine;
      }
      // Nolaノベル通知のタイトル
      return this.getNolaNovelTitle(notification as NotificationFromNolaNovel);
    },
    getNolaNovelTitle(notification: NotificationFromNolaNovel): string {
      const { type, novel, user, episode, company } = notification;

      switch (type) {
        case NotificationDataType.NOVEL_UPDATED:
          return `ブックマークしている${novel?.title || "作品"}が更新されました。`;
        case NotificationDataType.FOLLOW_NEWNOVEL:
          return `フォローしている${user?.name || "ユーザー"}さんの新作小説が投稿されました。`;
        case NotificationDataType.FOLLOW_NEWEPISODE:
          return `フォローしている${user?.name || "ユーザー"}さんの新作エピソードが投稿されました。`;
        case NotificationDataType.BOOKMARK:
          return `${novel?.title || "作品"}がブックマークされました！`;
        case NotificationDataType.REVIEW:
          return `${novel?.title || "作品"}に感想が届きました！`;
        case NotificationDataType.FOLLOW:
          return `${user?.name || "ユーザー"}さんにフォローされました！`;
        case NotificationDataType.COMMENT:
          return `エピソード「${episode?.title || ""}」にコメントが届きました！`;
        case NotificationDataType.COMMENT_REPLY:
          return `エピソード「${episode?.title || ""}」へのコメントに返信が届きました！`;
        case NotificationDataType.FOLLOW_NEWACTIVITY:
          return `フォローしている${user?.name || "ユーザー"}さんの活動報告が更新されました！`;
        case NotificationDataType.COMMENT_ACTIVITY:
          return "活動報告にコメントが届きました！";
        case NotificationDataType.COMMENT_ACTIVITY_REPLY:
          return "あなたの活動報告のコメントに返信が届きました";
        case NotificationDataType.FAVORITE_ACTIVITY:
          return "活動報告にいいね！がつきました！";
        case NotificationDataType.COMPANY_FOOTPRINTS:
          return `「${novel?.title || "作品"}」に${company?.name || "編集部"}の足あとがつきました！`;
        case NotificationDataType.COMPANY_FOOTPRINTS_EPISODE:
          return `「${novel?.title || "作品"}」のエピソードに${company?.name || "編集部"}の足あとがつきました！`;
        case NotificationDataType.COMPANY_BOOKMARK:
          return `「${novel?.title || "作品"}」に${company?.name || "編集部"}がブックマークしました！`;
        case NotificationDataType.COMPANY_FEATURED_NOVEL:
          return `「${novel?.title || "作品"}」が${company?.name || "編集部"}の注目作品に掲載されました！`;
        case NotificationDataType.ADMIN_PICKUP:
          return `「${novel?.title || "作品"}」が運営のピックアップに掲載されました！`;
        default:
          return "通知があります。";
      }
    },
    getNolaNovelContent(notification: Notification | NotificationFromNolaNovel): string {
      // 詳細コンテンツ（展開時に表示）
      return this.getNolaNovelTitle(notification as NotificationFromNolaNovel);
    },
    getAgentMessage(notification: Notification | NotificationFromNolaNovel): string {
      return (notification as Notification).message || "";
    },
    handleNolaNovelClick(notification: Notification | NotificationFromNolaNovel) {
      this.onNolaNovelClick(notification as NotificationFromNolaNovel);
    },
    isReaderNotification(notification: NotificationFromNolaNovel): boolean {
      return (
        notification.type === NotificationDataType.BOOKMARK ||
        notification.type === NotificationDataType.FOLLOW ||
        notification.type === NotificationDataType.REVIEW ||
        notification.type === NotificationDataType.COMMENT ||
        notification.type === NotificationDataType.COMMENT_REPLY ||
        notification.type === NotificationDataType.COMMENT_ACTIVITY ||
        notification.type === NotificationDataType.COMMENT_ACTIVITY_REPLY ||
        notification.type === NotificationDataType.FAVORITE_ACTIVITY
      );
    },
    isEditorNotification(notification: NotificationFromNolaNovel): boolean {
      return (
        notification.type === NotificationDataType.COMPANY_FOOTPRINTS ||
        notification.type === NotificationDataType.COMPANY_FOOTPRINTS_EPISODE ||
        notification.type === NotificationDataType.COMPANY_FEATURED_NOVEL ||
        notification.type === NotificationDataType.COMPANY_BOOKMARK ||
        notification.type === NotificationDataType.ADMIN_PICKUP
      );
    },
    getNolaNovelPath(notification: NotificationFromNolaNovel): string {
      const { type, novel, user, episode, comment, activity, company } = notification;

      switch (type) {
        case NotificationDataType.NOVEL_UPDATED:
        case NotificationDataType.FOLLOW_NEWNOVEL:
        case NotificationDataType.FOLLOW_NEWEPISODE:
        case NotificationDataType.BOOKMARK:
        case NotificationDataType.REVIEW:
          return `/novel/${novel?.id || ""}`;
        case NotificationDataType.FOLLOW:
          return `/user/${user?.id || ""}`;
        case NotificationDataType.COMMENT:
        case NotificationDataType.COMMENT_REPLY:
          return `/comments/${episode?.id || ""}?id=${comment?.id || ""}`;
        case NotificationDataType.FAVORITE_ACTIVITY:
          return `/activity/${activity?.id || ""}`;
        case NotificationDataType.COMMENT_ACTIVITY:
        case NotificationDataType.COMMENT_ACTIVITY_REPLY:
          return `/activity/${activity?.id || ""}?id=${comment?.id || ""}`;
        case NotificationDataType.FOLLOW_NEWACTIVITY:
          return `/activity/${activity?.id || ""}`;
        case NotificationDataType.COMPANY_BOOKMARK:
        case NotificationDataType.COMPANY_FOOTPRINTS:
        case NotificationDataType.COMPANY_FOOTPRINTS_EPISODE:
          return `/company/${company?.id || ""}`;
        case NotificationDataType.COMPANY_FEATURED_NOVEL:
          return `/?featuredCompany=${company?.id || ""}`;
        case NotificationDataType.ADMIN_PICKUP:
          return "/?id=adminPickup";
        default:
          return "/notification";
      }
    },
    onNolaNovelClick(notification: NotificationFromNolaNovel) {
      const path = this.getNolaNovelPath(notification);
      window.open(
        createUrlWithUtmParams(
          `${process.env.VUE_APP_NOLANOVEL_WEB}${path}`,
          NolaPageName.NolaNovelNotification,
          NolaItemId.NotificationItem
        ),
        process.env.VUE_APP_NOLANOVEL_NAME
      );
      if (!notification.isRead) {
        this.$store.dispatch("nolaNovelModule/readNotification", notification.typeTargetIdSubTargetId);
      }
    },
  },
});

interface Props {
  notifications: Notification[] | NotificationFromNolaNovel[];
  notificationType: NotificationType;
  isLoadComplete: boolean;
}

interface Data {
  initialUnreadIds: Set<string>;
}

interface Computed {}

interface Methods {
  getNotificationKey(notification: Notification | NotificationFromNolaNovel): string;
  isRead(notification: Notification | NotificationFromNolaNovel): boolean;
  isInitiallyUnread(notification: Notification | NotificationFromNolaNovel): boolean;
  formatDate(notification: Notification | NotificationFromNolaNovel): string;
  getCategoryLabel(notification: Notification | NotificationFromNolaNovel): string;
  getTitle(notification: Notification | NotificationFromNolaNovel): string;
  getNolaNovelTitle(notification: NotificationFromNolaNovel): string;
  getNolaNovelContent(notification: Notification | NotificationFromNolaNovel): string;
  getAgentMessage(notification: Notification | NotificationFromNolaNovel): string;
  handleNolaNovelClick(notification: Notification | NotificationFromNolaNovel): void;
  isReaderNotification(notification: NotificationFromNolaNovel): boolean;
  isEditorNotification(notification: NotificationFromNolaNovel): boolean;
  getNolaNovelPath(notification: NotificationFromNolaNovel): string;
  onNolaNovelClick(notification: NotificationFromNolaNovel): void;
}
